// Chess clock for Guess — გამოიცანი (pure, deterministic, replayable).
//
// Every player owns GUESS_TIME_MS for the whole match. Only the active player's clock runs, and
// only while a picture is on screen and nobody is waiting for the judge:
//   correct  → the clock stops at the END OF SPEECH and the turn passes to the next player;
//   wrong    → −3 s and a new picture, same player;
//   skip     → −3 s and a new picture, same player;
//   unclear / ignored → no penalty, same picture, the clock resumes;
//   error    → technical pause (no penalty) until the players resume;
//   silence  → nothing happens, the clock just keeps running.
// Time spent checking an answer is never charged. A clock that reaches 0 eliminates its player;
// the last player standing wins. The same reducer runs on the device and, from the event log,
// on the server when the match is saved.

export const GUESS_TIME_MS = 60_000;
export const GUESS_PENALTY_MS = 3_000;
export const GUESS_MAX_PLAYERS = 6;

export type ClockVerdict = "correct" | "wrong" | "skip" | "unclear" | "ignored" | "error";
export type ClockPhase = "ready" | "running" | "checking" | "transition" | "paused" | "over";
export type EndReason = "time" | "bank" | "abort";

export interface ClockPlayer {
  remaining: number;
  out: boolean;
  outAt: number | null; // match time (ms) when the player was eliminated
  correct: number;
  wrong: number;
  skips: number;
  unclear: number;
  /** Sum of think time (picture shown → end of speech) over correct answers. */
  thinkMs: number;
}

export interface ClockState {
  players: ClockPlayer[];
  active: number;
  phase: ClockPhase;
  /** When the active clock (re)started running; null when it is not running. */
  since: number | null;
  /** When the current picture appeared (for think time). */
  shownAt: number | null;
  /** The utterance being judged: its id and the end-of-speech time. */
  pending: { id: string; at: number } | null;
  /** Where to go when a pause ends. */
  resumeTo: Exclude<ClockPhase, "paused" | "over"> | null;
  pauseKind: "user" | "technical" | null;
  /** Index of the picture on screen (or about to be shown). */
  image: number;
  /** Utterance ids already resolved (late or duplicate results are ignored). */
  handled: string[];
  /** Order in which players were eliminated. */
  outOrder: number[];
  winner: number | null; // player index; -1 = draw
  endedBy: EndReason | null;
  startedAt: number | null;
  endedAt: number | null;
}

export type ClockEvent =
  | { t: "shown"; at: number; image: number }
  | { t: "speechEnd"; at: number; id: string }
  | { t: "verdict"; at: number; id: string; v: ClockVerdict }
  | { t: "tick"; at: number }
  | { t: "pause"; at: number; kind: "user" | "technical" }
  | { t: "resume"; at: number }
  | { t: "exhausted"; at: number }
  | { t: "abort"; at: number }
  /** A player left the match (online rooms): out at once, the others play on. */
  | { t: "forfeit"; at: number; player: number };

export function createClock(players: number, timeMs = GUESS_TIME_MS): ClockState {
  const n = Math.max(1, Math.min(GUESS_MAX_PLAYERS, Math.floor(players)));
  return {
    players: Array.from({ length: n }, () => ({ remaining: timeMs, out: false, outAt: null, correct: 0, wrong: 0, skips: 0, unclear: 0, thinkMs: 0 })),
    active: 0,
    phase: "ready",
    since: null,
    shownAt: null,
    pending: null,
    resumeTo: null,
    pauseKind: null,
    image: 0,
    handled: [],
    outOrder: [],
    winner: null,
    endedBy: null,
    startedAt: null,
    endedAt: null,
  };
}

const clone = (s: ClockState): ClockState => ({
  ...s,
  players: s.players.map((p) => ({ ...p })),
  pending: s.pending ? { ...s.pending } : null,
  handled: [...s.handled],
  outOrder: [...s.outOrder],
});

const alive = (s: ClockState) => s.players.map((p, i) => (p.out ? -1 : i)).filter((i) => i >= 0);

function nextAlive(s: ClockState, from: number): number {
  const n = s.players.length;
  for (let k = 1; k <= n; k++) {
    const i = (from + k) % n;
    if (!s.players[i].out) return i;
  }
  return from;
}

/** Remaining time of a player at `now`, never negative. */
export function remainingAt(s: ClockState, i: number, now: number): number {
  const p = s.players[i];
  if (!p) return 0;
  if (i === s.active && s.phase === "running" && s.since !== null) return Math.max(0, p.remaining - Math.max(0, now - s.since));
  return Math.max(0, p.remaining);
}

/** When the running clock will hit zero (for scheduling a tick), or null. */
export function expiresAt(s: ClockState): number | null {
  if (s.phase !== "running" || s.since === null) return null;
  return s.since + s.players[s.active].remaining;
}

function finish(s: ClockState, at: number, by: EndReason) {
  s.phase = "over";
  s.since = null;
  s.pending = null;
  s.endedBy = by;
  s.endedAt = at;
  const left = alive(s);
  if (by === "abort") s.winner = null;
  else if (left.length === 1) s.winner = left[0];
  else if (left.length === 0) s.winner = s.outOrder.length ? s.outOrder[s.outOrder.length - 1] : -1;
  else {
    // bank exhausted with several players alive: most time left wins; equal time = draw
    const best = Math.max(...left.map((i) => s.players[i].remaining));
    const top = left.filter((i) => s.players[i].remaining === best);
    s.winner = top.length === 1 ? top[0] : -1;
  }
}

function eliminate(s: ClockState, i: number, at: number) {
  const p = s.players[i];
  p.remaining = 0;
  p.out = true;
  p.outAt = at;
  s.outOrder.push(i);
  s.since = null;
  s.pending = null;
  if (alive(s).length <= 1) return finish(s, at, "time");
  s.active = nextAlive(s, i);
  s.phase = "transition"; // the next player gets a fresh picture
  s.image += 1;
}

/** Apply one event. Invalid or stale events leave the state unchanged. */
export function applyClock(prev: ClockState, e: ClockEvent): ClockState {
  if (prev.phase === "over") return prev;
  const s = clone(prev);
  switch (e.t) {
    case "shown": {
      if (s.phase !== "ready" && s.phase !== "transition") return prev;
      if (e.image !== s.image) return prev;
      s.phase = "running";
      s.since = e.at;
      s.shownAt = e.at;
      if (s.startedAt === null) s.startedAt = e.at;
      return s;
    }
    case "tick": {
      if (s.phase !== "running" || s.since === null) return prev;
      const p = s.players[s.active];
      if (e.at - s.since < p.remaining) return prev;
      eliminate(s, s.active, s.since + p.remaining);
      return s;
    }
    case "speechEnd": {
      if (s.phase !== "running" || s.since === null || s.pending) return prev;
      if (s.handled.includes(e.id)) return prev;
      const p = s.players[s.active];
      const at = Math.max(e.at, s.since);
      const used = at - s.since;
      if (used >= p.remaining) {
        // the time ran out before the player finished speaking
        eliminate(s, s.active, s.since + p.remaining);
        return s;
      }
      p.remaining -= used;
      s.since = null;
      s.phase = "checking";
      s.pending = { id: e.id, at };
      return s;
    }
    case "verdict": {
      if (!s.pending || s.pending.id !== e.id || s.handled.includes(e.id)) return prev;
      const paused = s.phase === "paused";
      if (s.phase !== "checking" && !paused) return prev;
      const p = s.players[s.active];
      const spokeAt = s.pending.at;
      s.handled.push(e.id);
      s.pending = null;
      let next: Exclude<ClockPhase, "paused" | "over">;
      switch (e.v) {
        case "correct": {
          p.correct += 1;
          if (s.shownAt !== null) p.thinkMs += Math.max(0, spokeAt - s.shownAt);
          s.active = nextAlive(s, s.active);
          s.image += 1;
          next = "transition";
          break;
        }
        case "wrong":
        case "skip": {
          if (e.v === "wrong") p.wrong += 1;
          else p.skips += 1;
          p.remaining = Math.max(0, p.remaining - GUESS_PENALTY_MS);
          if (p.remaining === 0) {
            eliminate(s, s.active, e.at);
            if (s.phase === "over") return s;
            if (paused) {
              s.resumeTo = "transition";
              s.phase = "paused";
            }
            return s;
          }
          s.image += 1;
          next = "transition";
          break;
        }
        case "unclear":
        case "ignored": {
          p.unclear += 1;
          next = "running";
          break;
        }
        case "error": {
          // technical problem: freeze everything until the players resume
          s.phase = "paused";
          s.pauseKind = "technical";
          s.resumeTo = "running";
          return s;
        }
      }
      if (paused) {
        s.resumeTo = next;
        return s;
      }
      s.phase = next;
      if (next === "running") s.since = e.at;
      return s;
    }
    case "pause": {
      if (s.phase === "paused" || s.phase === "ready") return prev;
      if (s.phase === "running" && s.since !== null) {
        const p = s.players[s.active];
        const used = Math.max(0, e.at - s.since);
        if (used >= p.remaining) {
          eliminate(s, s.active, s.since + p.remaining);
          return s;
        }
        p.remaining -= used;
        s.since = null;
      }
      s.resumeTo = s.phase as Exclude<ClockPhase, "paused" | "over">;
      s.phase = "paused";
      s.pauseKind = e.kind;
      return s;
    }
    case "resume": {
      if (s.phase !== "paused") return prev;
      const to = s.resumeTo ?? "running";
      s.phase = to;
      s.pauseKind = null;
      s.resumeTo = null;
      if (to === "running") s.since = e.at;
      return s;
    }
    case "exhausted": {
      if (s.phase !== "transition" && s.phase !== "ready") return prev;
      finish(s, e.at, "bank");
      return s;
    }
    case "abort": {
      finish(s, e.at, "abort");
      return s;
    }
    case "forfeit": {
      const i = e.player;
      const p = s.players[i];
      if (!p || p.out) return prev;
      if (i !== s.active) {
        p.out = true;
        p.outAt = e.at;
        s.outOrder.push(i);
        if (alive(s).length <= 1) finish(s, e.at, "time");
        return s;
      }
      if (s.phase === "running" && s.since !== null) {
        p.remaining = Math.max(0, p.remaining - Math.max(0, e.at - s.since));
      }
      if (s.phase === "ready") {
        // before the first picture: nobody's time has run, the next player simply starts
        p.out = true;
        p.outAt = e.at;
        s.outOrder.push(i);
        if (alive(s).length <= 1) finish(s, e.at, "time");
        else s.active = nextAlive(s, i);
        return s;
      }
      const wasPaused = s.phase === "paused";
      const keep = p.remaining;
      eliminate(s, i, e.at);
      p.remaining = keep; // a player who left keeps the time they had (for the results), but is out
      if (s.phase !== "over" && wasPaused) {
        s.resumeTo = "transition";
        s.phase = "paused";
      }
      return s;
    }
  }
  return prev;
}

/** Replay a whole event log (used by the server when a match is saved). */
export function replayClock(players: number, events: readonly ClockEvent[], timeMs = GUESS_TIME_MS): ClockState {
  let s = createClock(players, timeMs);
  for (const e of events) s = applyClock(s, e);
  return s;
}

/** Final ranking: winner first, then by elimination order (last eliminated ranks higher). */
export function ranking(s: ClockState): number[] {
  const n = s.players.length;
  const left = s.players.map((p, i) => (p.out ? -1 : i)).filter((i) => i >= 0);
  left.sort((a, b) => s.players[b].remaining - s.players[a].remaining);
  const out = [...s.outOrder].reverse();
  const order = [...left, ...out];
  if (s.winner !== null && s.winner >= 0) {
    const w = order.indexOf(s.winner);
    if (w > 0) order.unshift(...order.splice(w, 1));
  }
  return order.slice(0, n);
}
